import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { InputMaybe, SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";

import { QuestionForMain } from "@/components/features/_layouts/island/main/question";
import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import { FormErrorMessage, FormItem } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";
import { DatePickerRange } from "@/components/ui/input/date-picker-range";

type Props = {
	required?: boolean;

	isLimitedSale: boolean;

	saleTypeForTerm: ForNodeType<SaleTypeForTerm>;
	saleTypeForTermOptions: FormOptionType[];
	onSetSaleTypeForTerm: (input: string) => void;

	isShowSaleSection?: boolean;
	saleStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetSaleTerm: (input: DateRange) => void;

	isShowStayableSection?: boolean;
	stayableStart?: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableEnd?: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetStayableTerm?: (input: DateRange) => void;

	guide?: string;
	title?: string;
};

export const QuestionSalesTermWithQuestionForMain = memo(
	({
		required = false,

		isLimitedSale,

		isShowSaleSection = true,
		saleTypeForTerm,
		saleTypeForTermOptions,
		onSetSaleTypeForTerm,

		isShowStayableSection = true,
		saleStart,
		saleEnd,
		onSetSaleTerm,

		stayableStart,
		stayableEnd,
		onSetStayableTerm,

		title: _title,
		guide: _guide,
	}: Props) => {
		if (!isShowSaleSection && !isShowStayableSection) return null;

		const title = _title || "プランの販売期間";
		const guide =
			_guide ||
			"プランの販売期間を設定することができます。期間限定を選択した場合、公開期間中に予約された宿泊可能期間に滞在する予約でのみ、プランが購入可能になります。<br />この設定は在庫管理から上書き可能です。";

		return (
			<div className={cn(isLimitedSale && "space-y-6")}>
				<QuestionForMain
					required={required}
					title={title}
					guide={guide}
					inputChildren={
						<RadioGroupField
							value={saleTypeForTerm.value}
							options={saleTypeForTermOptions}
							handleValueChange={onSetSaleTypeForTerm}
						/>
					}
				/>

				<div className={cn(!isLimitedSale && "visually-hidden")}>
					<QuestionForMain
						required={required}
						title={""}
						inputChildren={
							<div className="space-y-10 py-4">
								{isShowSaleSection && (
									<FormItem>
										<div className="flex flex-col gap-y-1">
											<label className="text-sm">公開する期間</label>
											<DatePickerRange
												selected={{
													from: saleStart?.value ?? undefined,
													to: saleEnd?.value ?? undefined,
												}}
												onSelect={onSetSaleTerm}
											/>
										</div>

										<FormErrorMessage>{saleStart?.error || saleEnd?.error}</FormErrorMessage>
									</FormItem>
								)}
								{isShowStayableSection && onSetStayableTerm && (
									<FormItem>
										<div className="flex flex-col gap-y-1">
											<label className="text-sm">滞在可能な期間</label>
											<DatePickerRange
												selected={{
													from: stayableStart?.value ?? undefined,
													to: stayableEnd?.value ?? undefined,
												}}
												onSelect={onSetStayableTerm}
											/>
										</div>

										<FormErrorMessage>
											{stayableStart?.error || stayableEnd?.error}
										</FormErrorMessage>
									</FormItem>
								)}
							</div>
						}
					/>
				</div>
			</div>
		);
	},
);

QuestionSalesTermWithQuestionForMain.displayName = "QuestionSalesTermWithQuestionForMain";
